/**
 * GitHub admin handler
 * GitHub-specific proxy actions (extensible pattern for other integrations)
 */

import { useCallback } from "react";
import type { UseAdminAppReturn } from "../useAdminApp";

export interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  description: string;
  html_url: string;
  [key: string]: unknown;
}

export interface GitHubIssue {
  id: number;
  number: number;
  title: string;
  state: string;
  body: string;
  html_url: string;
  [key: string]: unknown;
}

export interface GitHubAdminHandler {
  proxyRequest: (participantId: string, endpoint: string, method?: string, data?: unknown) => Promise<unknown>;
  listRepos: (participantId: string) => Promise<GitHubRepo[]>;
  getRepo: (participantId: string, owner: string, repo: string) => Promise<GitHubRepo>;
  listIssues: (participantId: string, owner: string, repo: string) => Promise<GitHubIssue[]>;
  createIssue: (participantId: string, owner: string, repo: string, title: string, body?: string) => Promise<GitHubIssue>;
}

export function useGitHubAdmin(adminApp: UseAdminAppReturn): GitHubAdminHandler {
  // Generic proxy request
  const proxyRequest = useCallback(async (
    participantId: string,
    endpoint: string,
    method: string = 'GET',
    data?: unknown
  ): Promise<unknown> => {
    const response = await fetch(`${adminApp.httpUrl}/api/integrator/proxy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        participantId,
        integrationId: 'github', // GitHub integration ID
        endpoint,
        method,
        data,
      }),
    });

    if (!response.ok) {
      throw new Error(`GitHub proxy request failed: ${response.statusText}`);
    }

    return await response.json();
  }, [adminApp.httpUrl]);

  // List user's repositories
  const listRepos = useCallback(async (participantId: string): Promise<GitHubRepo[]> => {
    return await proxyRequest(participantId, '/user/repos') as GitHubRepo[];
  }, [proxyRequest]);

  // Get specific repository
  const getRepo = useCallback(async (
    participantId: string,
    owner: string,
    repo: string
  ): Promise<GitHubRepo> => {
    return await proxyRequest(participantId, `/repos/${owner}/${repo}`) as GitHubRepo;
  }, [proxyRequest]);

  // List repository issues
  const listIssues = useCallback(async (
    participantId: string,
    owner: string,
    repo: string
  ): Promise<GitHubIssue[]> => {
    return await proxyRequest(participantId, `/repos/${owner}/${repo}/issues`) as GitHubIssue[];
  }, [proxyRequest]);

  // Create issue
  const createIssue = useCallback(async (
    participantId: string,
    owner: string,
    repo: string,
    title: string,
    body?: string
  ): Promise<GitHubIssue> => {
    return await proxyRequest(
      participantId,
      `/repos/${owner}/${repo}/issues`,
      'POST',
      { title, body }
    ) as GitHubIssue;
  }, [proxyRequest]);

  return {
    proxyRequest,
    listRepos,
    getRepo,
    listIssues,
    createIssue,
  };
}

